import { useEffect } from "react";
import { ChevronRight } from "lucide-react";
import TopBar from "@/components/topbar";
import { useSheetStore } from "@/zustand/sheetStore";
import BinCard from "@/components/bin-card";
import { useGetBIN } from "@/hooks/use-queries";

// ── API shape ──────────────────────────────────────────────────────────────

type ApiBin = {
  id: number;
  bin: string;
  network: string;
  provider: string;
  price: number | string;
  defaultPrice: string;
  cardType: string;
  needDepositForActiveCard: boolean;
  needCardHolder: boolean;
  minDepositAmount: number;
  maxDepositAmount: number;
  topUpFee: number | string;
  isActive: boolean;
};

// ── BinCardData (used by binCards export and BinCard component) ────────────

type BinCardData = {
  id: number;
  name: string;
  image: string;
  price: number;
  displayPrice?: string;
  bin: string;
  binValue: string;
  logoImage?: string;
  payBadgeBackground?: string;
  network: string;
  cardType: string;
  provider: string;
  needDepositForActiveCard: boolean;
  needCardHolder: boolean;
  minDepositAmount: number;
  maxDepositAmount: number;
  topUpFee: number;
  linesImage?: string;
  blBlurImage?: string;
  trBlurImage?: string;
  background?: string;
  noiseImage?: string;
};

// ── Visual style constants — styling never comes from the API ──────────────

type CardVisualStyle = {
  logoImage: string;
  background: string;
  payBadgeBackground: string;
  linesImage?: string;
  blBlurImage?: string;
  trBlurImage?: string;
  supportTitle: string;
  noiseImage?: string;
};

const SAPPHIRE_STYLE: CardVisualStyle = {
  logoImage: "/images/gbin-logo.svg",
  background: "linear-gradient(180deg, #958595 0%, #996CAE 100%)",
  payBadgeBackground:
    "linear-gradient(90deg, rgba(180, 234, 255, 0.16) 0%, rgba(22, 27, 51, 0.16) 100%)",
  supportTitle: "Supported Usage Scenarios",
};

const PLATINUM_VISA_STYLE: CardVisualStyle = {
  logoImage: "/images/pbin-logo.svg",
  background: "linear-gradient(180deg, #8FCED9 0%, #5649AD 100%)",
  payBadgeBackground:
    "linear-gradient(100.47deg, rgba(87, 120, 199, 0.5) 9.36%, rgba(84, 90, 147, 0.5) 100%)",
  linesImage: "/images/platinum-card-lines.svg",
  blBlurImage: "/images/platinum-bl-blur.svg",
  trBlurImage: "/images/platinum-tr-blur.svg",
  supportTitle: "Supported Usage Scenarios",
};

const PLATINUM_MC_STYLE: CardVisualStyle = {
  logoImage: "/images/pbin-mastercard.svg",
  background:
    "linear-gradient(189.83deg, #DC9327 -67.19%, #E8B744 -46.21%, #F2D35B -25.23%, #F4DA22 -4.25%, #FEF375 16.73%, #F3C962 37.71%, #EAC34A 58.69%, #D99A26 79.67%, #B77E21 100.65%, #AB741F 121.63%, #A7711E 142.61%)",
  payBadgeBackground:
    "linear-gradient(155.06deg, rgba(136, 97, 48, 0.3) -8.11%, rgba(93, 52, 1, 0.3) 37.06%, rgba(23, 14, 0, 0.3) 93.93%)",
  linesImage: "/images/platinum-mastercard-line.svg",
  supportTitle: "Supported Usage Scenarios",
  blBlurImage: "/images/platinum-mastercard-bl-blur.svg",
  trBlurImage: "/images/platinum-mastercard-tr-blur.svg",
  noiseImage: "/images/platinum-bg.webp",
};

// Resolves visual style from the live card's provider + network — the shop tile
// and the create-card sheet both call this so they can never diverge.
export function cardStyle(
  provider: string,
  network: string,
): CardVisualStyle {
  if (provider === "int") return SAPPHIRE_STYLE;
  if (network === "Visa" || network === "VISA") return PLATINUM_VISA_STYLE;
  return PLATINUM_MC_STYLE;
}

// ── Static fallback binCards — consumed by create-platinum-card and others ─
// Prices/fees here are fallbacks only; the shop renders live API values.

export const binCards = {
  sapphire: {
    id: 537100,
    name: "Sapphire Card",
    image: "",
    price: 25,
    bin: "5371 0000 0000 0000",
    binValue: "537100",
    network: "MasterCard",
    cardType: "PrepaidCard",
    provider: "int",
    needDepositForActiveCard: true,
    needCardHolder: false,
    minDepositAmount: 0,
    maxDepositAmount: 1000000,
    topUpFee: 0,
    logoImage: SAPPHIRE_STYLE.logoImage,
    background: SAPPHIRE_STYLE.background,
    payBadgeBackground: SAPPHIRE_STYLE.payBadgeBackground,
  },
  platinum: {
    id: 111068,
    name: "Platinum Card",
    image: "",
    price: 35,
    bin: "4938 7519 0000 0000",
    binValue: "49387519",
    network: "Visa",
    cardType: "PrepaidCard",
    provider: "wsb",
    needDepositForActiveCard: true,
    needCardHolder: true,
    minDepositAmount: 1,
    maxDepositAmount: 1000000,
    topUpFee: 1,
    logoImage: PLATINUM_VISA_STYLE.logoImage,
    background: PLATINUM_VISA_STYLE.background,
    payBadgeBackground: PLATINUM_VISA_STYLE.payBadgeBackground,
    linesImage: PLATINUM_VISA_STYLE.linesImage,
    blBlurImage: PLATINUM_VISA_STYLE.blBlurImage,
    trBlurImage: PLATINUM_VISA_STYLE.trBlurImage,
  },
  platinumBasic: {
    id: 111078,
    name: "Platinum Card",
    image: "",
    price: 35,
    bin: "5240 1300 0000 0000",
    binValue: "524013",
    network: "MasterCard",
    cardType: "PrepaidCard",
    provider: "wsb",
    needDepositForActiveCard: true,
    needCardHolder: false,
    minDepositAmount: 0,
    maxDepositAmount: 1000000,
    topUpFee: 1,
    logoImage: PLATINUM_MC_STYLE.logoImage,
    background: PLATINUM_MC_STYLE.background,
    payBadgeBackground: PLATINUM_MC_STYLE.payBadgeBackground,
    linesImage: PLATINUM_MC_STYLE.linesImage,
    blBlurImage: PLATINUM_MC_STYLE.blBlurImage,
    trBlurImage: PLATINUM_MC_STYLE.trBlurImage,
    noiseImage: PLATINUM_MC_STYLE.noiseImage,
  },
} satisfies Record<string, BinCardData>;

export function getCardStyle(
  bin: string,
  network?: string,
): {
  background: string;
  linesImage: string;
  noiseImage?: string;
  logoImage: string;
} {
  const match = Object.values(binCards).find((c) => c.binValue === bin);
  if (match) {
    const s = cardStyle(match.provider, match.network);
    return {
      background: s.background,
      linesImage: s.linesImage ?? "/images/card-lines.svg",
      noiseImage: s.noiseImage,
      logoImage: s.logoImage,
    };
  }
  if (network === "VISA" || network === "Visa")
    return {
      background: PLATINUM_VISA_STYLE.background,
      linesImage: PLATINUM_VISA_STYLE.linesImage!,
      noiseImage: PLATINUM_VISA_STYLE.noiseImage,
      logoImage: PLATINUM_VISA_STYLE.logoImage,
    };
  if (network === "MasterCard")
    return {
      background: PLATINUM_MC_STYLE.background,
      linesImage: PLATINUM_MC_STYLE.linesImage!,
      noiseImage: PLATINUM_MC_STYLE.noiseImage,
      logoImage: PLATINUM_MC_STYLE.logoImage,
    };
  return {
    background: SAPPHIRE_STYLE.background,
    linesImage: "/images/card-lines.svg",
    noiseImage: SAPPHIRE_STYLE.noiseImage,
    logoImage: SAPPHIRE_STYLE.logoImage,
  };
}

// ── Skeleton ───────────────────────────────────────────────────────────────

const ShopCardSkeleton = () => (
  <div className="bg-dark-card-3 relative h-full animate-pulse rounded-2xl border border-[#E8EDF3] p-3 shadow-sm sm:p-4">
    <div className="h-[180px] rounded-2xl bg-gray-200" />
    <div className="mt-3 space-y-2">
      <div className="h-2.5 w-1/3 rounded-full bg-gray-200" />
      <div className="h-8 w-full rounded bg-gray-200" />
    </div>
    <div className="mt-3 flex items-center justify-between border-t border-[#E8EDF3] pt-3 sm:mt-4">
      <div className="h-5 w-24 rounded bg-gray-200" />
      <div className="size-4 rounded bg-gray-200" />
    </div>
  </div>
);

// ── Shop ───────────────────────────────────────────────────────────────────

const Shop = () => {
  const { openSheet } = useSheetStore();
  const { data: binsData, isLoading } = useGetBIN();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const activeBins: ApiBin[] = (() => {
    const raw = binsData;
    const arr: ApiBin[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
    return arr.filter((b) => b.isActive);
  })();

  return (
    <div>
      <TopBar title="Back" backTo="/dashboard/cards" />

      <div className="px-4 pt-4 pb-8 lg:px-6 lg:pt-6">
        <div className="bg-dark-card-3 relative overflow-hidden rounded-2xl px-4 py-4 shadow-sm sm:px-5 sm:py-5">
          <p className="text-dark-text-400 text-sm leading-5 sm:text-base sm:leading-6">
            Buy cards for seamless online and offline use on Vaulta.
          </p>
          <div className="absolute top-0 right-0 h-full w-20 overflow-hidden rounded-r-2xl opacity-10 sm:w-24 lg:w-32">
            <img
              src="/images/logo-transparent-light.svg"
              alt=""
              className="absolute top-1/2 right-2 h-12 w-12 -translate-y-1/2 sm:right-3 sm:h-16 sm:w-16"
            />
          </div>
        </div>

        <div className="mx-auto mt-4 grid max-w-[430px] gap-3 sm:mt-5 sm:gap-4 md:max-w-none lg:grid-cols-3">
          {isLoading ? (
            [0, 1, 2].map((i) => <ShopCardSkeleton key={i} />)
          ) : (
            <>
              {activeBins.map((bin) => {
                const style = cardStyle(bin.provider, bin.network);
                const cardData: BinCardData = {
                  id: bin.id,
                  name: "Virtual Card",
                  image: "",
                  price: Number(bin.price),
                  displayPrice:
                    bin.provider === "wsb"
                      ? `${bin.price} - ${bin.defaultPrice} USD`
                      : undefined,
                  bin: bin.bin,
                  binValue: bin.bin,
                  network: bin.network,
                  cardType: bin.cardType,
                  provider: bin.provider,
                  needDepositForActiveCard: bin.needDepositForActiveCard,
                  needCardHolder: bin.needCardHolder,
                  minDepositAmount: bin.minDepositAmount,
                  maxDepositAmount: bin.maxDepositAmount,
                  topUpFee: Number(bin.topUpFee),
                  logoImage: style.logoImage,
                  background: style.background,
                  payBadgeBackground: style.payBadgeBackground,
                  linesImage: style.linesImage,
                  blBlurImage: style.blBlurImage,
                  trBlurImage: style.trBlurImage,
                  noiseImage: style.noiseImage,
                };

                const supportBody =
                  bin.provider === "int" ? (
                    <>
                      The card{" "}
                      <strong style={{ color: "#B37AF2" }}>DOES NOT</strong>{" "}
                      currently support Uber, Petrol stations, Talabat, Hermes
                      Amsterdam, Hermes Paris, and Iberia and a few other
                      merchants.
                    </>
                  ) : (
                    <>
                      The card{" "}
                      <strong style={{ color: "#B37AF2" }}>SUPPORTS</strong>{" "}
                      {/* Uber isn't supported on the Visa platinum card. */}
                      {/visa/i.test(bin.network ?? "") ? "" : "Uber, "}
                      petrol stations, YouTube, AliExpress, Amazon, Iberia,
                      Talabat.
                    </>
                  );

                return (
                  <button
                    key={bin.id}
                    onClick={() => {
                      if (bin.provider === "int") {
                        openSheet("createSapphireCard", 2, {}, false);
                      } else {
                        openSheet(
                          "createPlatinumCard",
                          2,
                          {
                            bin: {
                              id: Number(bin.id),
                              bin: bin.bin,
                              provider: bin.provider,
                              network: bin.network,
                              price: Number(bin.price),
                              defaultPrice: Number(bin.defaultPrice),
                              topUpFee: Number(bin.topUpFee),
                            },
                          },
                          false,
                        );
                      }
                    }}
                    className="block h-full w-full cursor-pointer text-left"
                  >
                    <div className="group bg-dark-card-3 relative h-full rounded-2xl border border-[#E8EDF3] p-3 shadow-sm sm:p-4">
                      <BinCard
                        card={cardData}
                        className="!h-[180px] rounded-2xl"
                      />
                      <div className="mt-3 space-y-1.5">
                        <p className="text-[11px] text-[#9CA3AF]">
                          {style.supportTitle}
                        </p>
                        <p className="text-dark-text-300 min-h-10 text-[11px] leading-4 sm:min-h-12 sm:text-xs">
                          {supportBody}
                        </p>
                      </div>
                      <div className="mt-3 flex items-center justify-between border-t border-[#E8EDF3] pt-3 sm:mt-4">
                        <span className="text-dark-text-400 text-base font-semibold sm:text-lg">
                          Virtual Card
                        </span>
                        <ChevronRight className="text-dark-primary-main size-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </div>
                    </div>
                  </button>
                );
              })}

              {/* Physical card — always coming soon */}
              <div className="h-full">
                <div className="bg-dark-card-3 relative h-full rounded-2xl border border-[#E8EDF3] p-3 shadow-sm sm:p-4">
                  <div className="h-[180px] overflow-hidden rounded-2xl bg-gradient-to-br from-slate-600 to-slate-800" />
                  <div className="mt-3 space-y-1.5">
                    <p className="text-[11px] text-[#9CA3AF]">
                      Supported Usage Scenarios
                    </p>
                    <p className="text-dark-text-300 min-h-10 text-[11px] leading-4 sm:min-h-12 sm:text-xs">
                      Everyday use for both online and offline purchase and ATM
                    </p>
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-[#E8EDF3] pt-3 sm:mt-4">
                    <span className="text-dark-text-400 text-base font-semibold sm:text-lg">
                      Physical Card
                    </span>
                    <span className="rounded-full border border-[#E8EDF3] bg-[#F5F7FA] px-3 py-1 text-[10px] tracking-[0.18em] text-[#9CA3AF] uppercase">
                      Coming soon
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
